import { NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, ClipboardList, Building2, Users, Sparkles, ScrollText, DatabaseBackup, UserCircle, Map, AlertTriangle, ClipboardCheck, Truck, FileText, ListChecks, Repeat } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";
import { TothPanel } from "@/components/TothPanel";
import { ROLE_LABEL } from "@/lib/format";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: null },
  { to: "/ordini", label: "Ordini di lavoro", icon: ClipboardList, roles: null },
  { to: "/ricorrenze", label: "Ordini ricorrenti", icon: Repeat, roles: ["ceo", "amministrazione", "responsabile"] },
  { to: "/mappe", label: "Mappe", icon: Map, roles: null },
  { to: "/criticita", label: "Criticità", icon: AlertTriangle, roles: null },
  { to: "/ispezioni", label: "Ispezioni", icon: ClipboardCheck, roles: null },
  { to: "/mezzi", label: "Mezzi e attrezzature", icon: Truck, roles: null },
  { to: "/documenti", label: "Documenti", icon: FileText, roles: ["ceo", "amministrazione", "responsabile"] },
  { to: "/obblighi", label: "Obblighi contrattuali", icon: ListChecks, roles: ["ceo", "amministrazione", "responsabile"] },
  { to: "/commesse", label: "Commesse", icon: Building2, roles: ["ceo", "amministrazione", "responsabile", "caposquadra", "manutenzione", "sicurezza", "qualita"] },
  { to: "/personale", label: "Personale", icon: Users, roles: ["ceo", "amministrazione", "responsabile"] },
  { to: "/toth", label: "Toth", icon: Sparkles, roles: ["ceo", "amministrazione"] },
  { to: "/audit", label: "Registro audit", icon: ScrollText, roles: ["ceo", "amministrazione"] },
  { to: "/admin", label: "Backup e dati", icon: DatabaseBackup, roles: ["ceo", "amministrazione"] },
  { to: "/profilo", label: "Profilo", icon: UserCircle, roles: null },
];
const MOBILE = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/ordini", label: "Ordini", icon: ClipboardList },
  { to: "/mappe", label: "Mappe", icon: Map },
  { to: "/criticita", label: "Criticità", icon: AlertTriangle },
  { to: "/profilo", label: "Profilo", icon: UserCircle },
];

const Sidebar = ({ user }) => (
  <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r bg-white lg:flex">
    <div className="px-6 py-6">
      <img src="/brand/logo-h.png" alt="DR Multiservice" className="h-7 w-auto" data-testid="sidebar-logo" />
      <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">DR APP · Direzione operativa</p>
    </div>
    <nav className="flex-1 space-y-1 px-3">
      {NAV.filter((n) => !n.roles || n.roles.includes(user.role)).map((n) => (
        <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.to.replace("/", "") || "dashboard"}`}
          className={({ isActive }) => cn("flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors",
            isActive ? "bg-accent text-[hsl(var(--brand-dark))]" : "text-muted-foreground hover:bg-muted hover:text-foreground")}>
          <n.icon className="h-[18px] w-[18px]" /> {n.label}
        </NavLink>
      ))}
    </nav>
    <div className="m-3 rounded-2xl bg-muted p-3">
      <p className="truncate text-sm font-bold" data-testid="sidebar-user-name">{user.name}</p>
      <p className="text-xs text-muted-foreground">{ROLE_LABEL[user.role]}</p>
    </div>
  </aside>
);

const BottomNav = () => (
  <nav className="fixed inset-x-0 bottom-0 z-40 flex h-[72px] items-start justify-around border-t bg-white/95 pt-2 backdrop-blur-md lg:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
    {MOBILE.map((n) => (
      <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`mobile-nav-${n.to.replace("/", "") || "dashboard"}`}
        className={({ isActive }) => cn("flex w-16 flex-col items-center gap-1 text-[11px] font-semibold", isActive ? "text-[hsl(var(--brand))]" : "text-zinc-400")}>
        <n.icon className="h-6 w-6" /> {n.label}
      </NavLink>
    ))}
  </nav>
);

export default function AppShell() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen">
      <Sidebar user={user} />
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-3 border-b bg-background/85 px-4 backdrop-blur-md sm:px-6 lg:px-8">
          <img src="/brand/logo-h.png" alt="DR Multiservice" className="h-6 w-auto lg:hidden" />
          <span className="hidden text-sm font-semibold text-muted-foreground lg:block">D.R. Multiservice srl</span>
          <div className="flex items-center gap-2">
            {user?.demo_data && <span className="hidden rounded-full bg-zinc-900 px-2.5 py-1 text-[10px] font-bold tracking-wider text-white sm:inline" data-testid="demo-env-badge">AMBIENTE DEMO</span>}
            <NotificationBell />
          </div>
        </header>
        <main className="mx-auto max-w-[1400px] px-4 py-6 safe-bottom sm:px-6 lg:px-8 lg:pb-12">
          <Outlet />
        </main>
      </div>
      <BottomNav />
      {["ceo", "amministrazione"].includes(user?.role) && <TothPanel />}
    </div>
  );
}
